import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Container from "../../../components/Container";
import { useTranslation } from "react-i18next";
import { Lock, Paperclip, Search, User } from "lucide-react";
import axios from "axios";

/** 날짜 포맷터 (YYYY-MM-DD) */
const fmtDate = (d) => {
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** 로컬스토리지 키 */
const FINDER_KEY = "inquiryFinder"; // { email?, phone?, password? }

export default function Inquiry() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();

  // ---- 검색/필터 상태 ----
  const [keyword, setKeyword] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);

  // “내 글 찾기” 폼 상태
  const [finderEmail, setFinderEmail] = useState("");
  const [finderPhone, setFinderPhone] = useState("");
  const [finderPassword, setFinderPassword] = useState("");

  // 페이지네이션 상태
  const [page, setPage] = useState(1); // 1-base
  const pageSize = 10;

  // 데이터 상태
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const allItemsRef = useRef([]); // 서버에서 받아온 원본(필터/페이지 계산용)
  const abortRef = useRef(null); // 요청 취소 컨트롤러

  // 최초 로드 시 기존 finder 불러오기
  useEffect(() => {
    try {
      const raw = localStorage.getItem(FINDER_KEY);
      if (raw) {
        const v = JSON.parse(raw);
        setFinderEmail(v.email || "");
        setFinderPhone(v.phone || "");
        setFinderPassword(v.password || "");
      }
    } catch {}
  }, []);

  // ------- 실제 API 붙일 곳 -------
  async function fetchBoards({ page, pageSize, keyword, finder, onlyMine }) {
    // 이전 요청 취소
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    try {
      // 1) 서버에서 전체 리스트 1회 수신
      const res = await axios.get("/api/inquiry/list", {
        signal: abortRef.current.signal,
      });

      // 응답 형태: ExceptionApi<List<InquiryResponse>>
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      // 필드 매핑: 목록 화면에서 쓰는 최소 정보만 추려 저장
      const mapped = list.map((it) => ({
        id: it.inquiryId,
        title: it.title,
        author: it.author,
        createdAt: it.createdAt,
        secret: !!it.secret,
        // 첨부 개수: 단일 경로면 0/1, 콤마구분 등으로 오면 개수로 환산
        attachments: !it.attachmentPath
          ? 0
          : String(it.attachmentPath).split(",").filter(Boolean).length,
        // “내 글” 여부는 서버가 판단해주는 게 최선이지만,
        // 현재는 finder(이메일/폰비밀번호)가 있을 때 임시 표기 규칙 없이 false로 둠
        mine: false,
      }));

      allItemsRef.current = mapped;

      // 2) 필터  페이지네이션 계산
      applyFilterAndPaging({ page, pageSize, keyword, finder, onlyMine });
    } catch (err) {
      if (
        err?.code === "ERR_CANCELED" ||
        err?.message === "canceled" ||
        err?.name === "CanceledError"
      ) {
        return; // 취소된 요청 무시
      }
      console.error(err);
      allItemsRef.current = [];
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }

  function applyFilterAndPaging({ page, pageSize, keyword, finder, onlyMine }) {
    const all = allItemsRef.current;

    // 키워드(제목/작성자)
    const filteredByKeyword = !keyword.trim()
      ? all
      : all.filter((x) => {
          const n = keyword.toLowerCase();
          return (
            String(x.title).toLowerCase().includes(n) ||
            String(x.author).toLowerCase().includes(n)
          );
        });

    // 내 글만 (서버가 판정해주는 게 베스트. 프론트 임시 기능은 off)
    const filtered = onlyMine
      ? filteredByKeyword.filter((x) => x.mine)
      : filteredByKeyword;

    const start = (page - 1) * pageSize;
    const slice = filtered.slice(start, start + pageSize);

    setItems(slice);
    setTotalCount(filtered.length);
  }

  // 목록 로딩
  useEffect(() => {
    setLoading(true);
    const finder = {
      email: finderEmail.trim(),
      phone: finderPhone.trim(),
      password: finderPassword.trim(),
    };
    fetchBoards({ page, pageSize, keyword, finder, onlyMine });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, keyword, onlyMine, i18n.language]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const numberedRows = useMemo(() => {
    return items.map((row, idx) => {
      const number = totalCount - (page - 1) * pageSize - idx;
      return { ...row, number };
    });
  }, [items, page, pageSize, totalCount]);

  // 내 글 저장
  const saveFinder = () => {
    const payload = {
      email: finderEmail.trim(),
      phone: finderPhone.trim(),
      password: finderPassword.trim(),
    };
    localStorage.setItem(FINDER_KEY, JSON.stringify(payload));
    // 저장 즉시 “내 글만 보기” 켜고 1페이지로 이동
    setOnlyMine(true);
    setPage(1);
  };

  // 내 글 초기화
  const clearFinder = () => {
    localStorage.removeItem(FINDER_KEY);
    setFinderEmail("");
    setFinderPhone("");
    setFinderPassword("");
    setOnlyMine(false);
    setPage(1);
  };

  return (
    <div className="text-blue-900">
      {/* 상단 타이틀 + 검색/내글패널 */}
      <section className="py-8 md:py-12">
        <Container>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">
                {t("inquiryList.title")}
              </h1>
              {t("inquiryList.desc") && (
                <p className="mt-2 text-slate-600">{t("inquiryList.desc")}</p>
              )}
            </div>

            {/* 검색바 */}
            <div className="w-full max-w-md">
              <label className="sr-only" htmlFor="keyword">
                {t("inquiryList.searchPH")}
              </label>
              <div className="flex items-center gap-2 rounded-xl border bg-white px-3 py-2 shadow-sm">
                <Search className="h-4 w-4 text-slate-500" />
                <input
                  id="keyword"
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    setPage(1);
                  }}
                  placeholder={t("inquiryList.searchPH")}
                  className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                />
              </div>
            </div>
          </div>

          {/* 내 글 찾기 패널 */}
          {/* <div className="mt-6 rounded-xl border bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700">
                    {t("inquiryList.finder.email")}
                  </label>
                  <input
                    type="email"
                    value={finderEmail}
                    onChange={(e) => setFinderEmail(e.target.value)}
                    placeholder={t("inquiryList.finder.phEmail")}
                    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700">
                    {t("inquiryList.finder.phone")}
                  </label>
                  <input
                    type="tel"
                    value={finderPhone}
                    onChange={(e) => setFinderPhone(e.target.value)}
                    placeholder={t("inquiryList.finder.phPhone")}
                    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700">
                    {t("inquiryList.finder.password")}
                  </label>
                  <input
                    type="password"
                    value={finderPassword}
                    onChange={(e) => setFinderPassword(e.target.value)}
                    placeholder={t("inquiryList.finder.phPassword")}
                    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={onlyMine}
                    onChange={(e) => {
                      setOnlyMine(e.target.checked);
                      setPage(1);
                    }}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  {t("inquiryList.finder.onlyMine")}
                </label>

                <button
                  type="button"
                  onClick={saveFinder}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  {t("inquiryList.finder.save")}
                </button>
                <button
                  type="button"
                  onClick={clearFinder}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                >
                  {t("inquiryList.finder.clear")}
                </button>
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              {t("inquiryList.finder.note")}
            </p>
          </div> */}
        </Container>
      </section>

      {/* 게시판 리스트 */}
      <section className="pb-16">
        <Container>
          <div className="relative">
            {/* 테이블 */}
            <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
              <div className="grid grid-cols-[70px_1fr_160px_140px] items-center border-b bg-slate-50/60 px-3 py-3 text-center text-[15px] font-semibold text-slate-900 md:grid-cols-[90px_1fr_180px_160px] md:px-4 md:text-base">
                <div>{t("inquiryList.th.no")}</div>
                <div className="text-left md:text-center">
                  {t("inquiryList.th.title")}
                </div>
                <div>{t("inquiryList.th.author")}</div>
                <div>{t("inquiryList.th.date")}</div>
              </div>

              {/* 내용 */}
              {loading ? (
                <div className="flex h-60 items-center justify-center text-sm text-slate-500">
                  {t("inquiryList.loading")}
                </div>
              ) : numberedRows.length === 0 ? (
                <div className="flex h-60 items-center justify-center text-sm text-slate-500">
                  {t("inquiryList.empty")}
                </div>
              ) : (
                <ul className="divide-y">
                  {numberedRows.map((row) => (
                    <li
                      key={row.id}
                      className="grid grid-cols-[70px_1fr_160px_140px] items-center px-3 py-3 text-sm md:grid-cols-[90px_1fr_180px_160px] md:px-4 md:text-[15px]"
                    >
                      <div className="text-center text-slate-700">
                        {row.number}
                      </div>

                      {/* 제목 셀 */}
                      <button
                        className="min-w-0 truncate text-left font-medium text-slate-900 hover:underline"
                        onClick={() => nav(`/inquiry/${row.id}`)}
                        title={row.title}
                      >
                        <span className="inline-flex items-center gap-2">
                          {/* 비밀글 아이콘 */}
                          {row.secret && (
                            <span
                              className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700 ring-1 ring-slate-200"
                              title={t("inquiryList.secret")}
                            >
                              <Lock className="h-3.5 w-3.5" />
                              {t("inquiryList.secret")}
                            </span>
                          )}
                          {/* 내 글 배지 */}
                          {row.mine && (
                            <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-1.5 py-0.5 text-[11px] text-blue-700 ring-1 ring-blue-200">
                              <User className="h-3.5 w-3.5" />
                              {t("inquiryList.mine")}
                            </span>
                          )}

                          {/* 제목 */}
                          <span className="truncate">
                            {row.secret
                              ? t("inquiryList.secretTitle")
                              : row.title}
                          </span>

                          {/* 첨부 */}
                          {row.attachments > 0 && (
                            <span
                              className="inline-flex items-center gap-1 text-slate-500"
                              title={t("inquiryList.attach", {
                                n: row.attachments,
                              })}
                            >
                              <Paperclip className="h-4 w-4" />
                              <span className="text-[11px]">
                                {row.attachments}
                              </span>
                            </span>
                          )}
                        </span>
                      </button>

                      <div className="text-center text-slate-700">
                        {row.author}
                      </div>
                      <div className="text-center text-slate-700">
                        {fmtDate(row.createdAt)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* 페이지네이션 */}
            <div className="mt-8 flex flex-col items-center justify-between gap-3 md:flex-row">
              <Link
                to="/inquiry/new"
                className="order-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 md:order-1"
              >
                {t("inquiryList.write")}
              </Link>

              <nav
                className="order-1 isolate inline-flex overflow-hidden rounded-xl border bg-white shadow-sm md:order-2"
                aria-label={t("inquiryList.pagination")}
              >
                <button
                  className={`px-4 py-2 text-sm ${
                    page > 1
                      ? "hover:bg-slate-50"
                      : "cursor-not-allowed opacity-40"
                  }`}
                  onClick={() => page > 1 && setPage(1)}
                  disabled={!(page > 1)}
                  aria-label={t("inquiryList.first")}
                >
                  «
                </button>
                <button
                  className={`border-l px-4 py-2 text-sm ${
                    page > 1
                      ? "hover:bg-slate-50"
                      : "cursor-not-allowed opacity-40"
                  }`}
                  onClick={() => page > 1 && setPage((p) => p - 1)}
                  disabled={!(page > 1)}
                  aria-label={t("inquiryList.prev")}
                >
                  ‹
                </button>

                <span className="border-l px-4 py-2 text-sm text-slate-700">
                  {page} / {Math.max(1, Math.ceil(totalCount / pageSize))}
                </span>

                <button
                  className={`border-l px-4 py-2 text-sm ${
                    page < Math.max(1, Math.ceil(totalCount / pageSize))
                      ? "hover:bg-slate-50"
                      : "cursor-not-allowed opacity-40"
                  }`}
                  onClick={() =>
                    page < Math.max(1, Math.ceil(totalCount / pageSize)) &&
                    setPage((p) => p + 1)
                  }
                  disabled={
                    !(page < Math.max(1, Math.ceil(totalCount / pageSize)))
                  }
                  aria-label={t("inquiryList.next")}
                >
                  ›
                </button>
                <button
                  className={`border-l px-4 py-2 text-sm ${
                    page < Math.max(1, Math.ceil(totalCount / pageSize))
                      ? "hover:bg-slate-50"
                      : "cursor-not-allowed opacity-40"
                  }`}
                  onClick={() =>
                    page < Math.max(1, Math.ceil(totalCount / pageSize)) &&
                    setPage(Math.max(1, Math.ceil(totalCount / pageSize)))
                  }
                  disabled={
                    !(page < Math.max(1, Math.ceil(totalCount / pageSize)))
                  }
                  aria-label={t("inquiryList.last")}
                >
                  »
                </button>
              </nav>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
